import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, Circle, Plus, Trash2, GraduationCap, Users } from "lucide-react";
import SubjectsTab from "../components/certif/SubjectsTab";
import QcmTab from "../components/certif/QcmTab";
import DeliberationTab from "../components/certif/DeliberationTab";
import DocumentsTab from "../components/certif/DocumentsTab";
import { Textarea } from "../components/ui/textarea";

const inputCls = "bg-[#0b0811] border-white/10 text-white";
const STATUT_BADGE = {
  preparation: { label: "Préparation", cls: "bg-sky-500/15 text-sky-300" },
  planifiee: { label: "Planifiée", cls: "bg-amber-500/15 text-amber-300" },
  en_cours: { label: "En cours", cls: "bg-emerald-500/15 text-emerald-300" },
  cloturee: { label: "Clôturée", cls: "bg-white/10 text-slate-400" },
};

export default function ExamSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/certif/sessions/${sessionId}`);
      setSession(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Session introuvable");
    } finally { setLoading(false); }
  }, [sessionId]);
  useEffect(() => { reload(); }, [reload]);

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>;
  if (!session) return <div className="p-10 text-center text-slate-500">Session introuvable.</div>;

  const cert = session.certification || {};
  const badge = STATUT_BADGE[session.statut] || STATUT_BADGE.preparation;
  const done = (session.checklist || []).filter(c => c.done).length;

  return (
    <div className="p-6 max-w-[1280px] mx-auto" data-testid="exam-session-page">
      <button onClick={() => navigate("/admin/examens")} className="text-xs text-slate-400 hover:text-white flex items-center gap-1 mb-3" data-testid="back-to-exams-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour aux examens
      </button>
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <GraduationCap className="w-6 h-6 text-[#f7c935]" />
          <h1 className="text-xl font-bold text-white">{cert.code ? `${cert.code} — ` : ""}{cert.titre}</h1>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${badge.cls}`} data-testid="session-statut-badge">{badge.label}</span>
          <span className="text-xs text-slate-500 ml-auto">Checklist {done}/{(session.checklist || []).length}</span>
        </div>
        <div className="grid md:grid-cols-3 gap-2 mt-3">
          <div><label className="text-[10px] uppercase text-slate-500">Date d'examen</label>
            <Input type="date" className={inputCls} defaultValue={session.date_examen}
              onBlur={async e => { await api.put(`/certif/sessions/${sessionId}`, { date_examen: e.target.value }); }} data-testid="session-edit-date" /></div>
          <div><label className="text-[10px] uppercase text-slate-500">Lieu</label>
            <Input className={inputCls} defaultValue={session.lieu}
              onBlur={async e => { await api.put(`/certif/sessions/${sessionId}`, { lieu: e.target.value }); }} data-testid="session-edit-lieu" /></div>
          <div><label className="text-[10px] uppercase text-slate-500">Responsable</label>
            <Input className={inputCls} defaultValue={session.responsable}
              onBlur={async e => { await api.put(`/certif/sessions/${sessionId}`, { responsable: e.target.value }); }} data-testid="session-edit-responsable" /></div>
        </div>
        <div className="mt-2">
          <label className="text-[10px] uppercase text-slate-500">Contexte / matériel disponible (base de la génération IA des sujets)</label>
          <Textarea rows={2} className={`${inputCls} text-xs`} defaultValue={session.contexte_materiel}
            onBlur={async e => { await api.put(`/certif/sessions/${sessionId}`, { contexte_materiel: e.target.value }); }} data-testid="session-edit-contexte" />
        </div>
      </div>

      <Tabs defaultValue="checklist">
        <TabsList className="bg-[#14101c] border border-white/5 flex-wrap h-auto">
          <TabsTrigger value="checklist" data-testid="tab-checklist">Checklist</TabsTrigger>
          <TabsTrigger value="candidats" data-testid="tab-candidats">Candidats & Jury ({(session.candidates || []).length})</TabsTrigger>
          <TabsTrigger value="sujets" data-testid="tab-sujets">Sujets ({(session.sujets || []).length})</TabsTrigger>
          <TabsTrigger value="qcm" data-testid="tab-qcm">QCM</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="deliberation" data-testid="tab-deliberation">Délibération</TabsTrigger>
        </TabsList>

        <TabsContent value="checklist">
          <ChecklistTab api={api} session={session} onChange={reload} />
        </TabsContent>
        <TabsContent value="candidats">
          <CandidatesJuryTab api={api} session={session} onChange={reload} />
        </TabsContent>
        <TabsContent value="sujets">
          <SubjectsTab api={api} session={session} cert={cert} onChange={reload} />
        </TabsContent>
        <TabsContent value="qcm">
          <QcmTab api={api} session={session} cert={cert} onChange={reload} />
        </TabsContent>
        <TabsContent value="documents">
          <DocumentsTab api={api} session={session} onChange={reload} />
        </TabsContent>
        <TabsContent value="deliberation">
          <DeliberationTab api={api} session={session} cert={cert} onChange={reload} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ChecklistTab({ api, session, onChange }) {
  const toggle = async (item) => {
    await api.put(`/certif/sessions/${session.session_id}/checklist/${item.key}`, { done: !item.done });
    onChange();
  };
  return (
    <div className="space-y-1.5" data-testid="session-checklist">
      {(session.checklist || []).map(item => (
        <button key={item.key} onClick={() => toggle(item)}
          className={`w-full text-left flex items-center gap-3 rounded-lg border p-3 transition-colors ${item.done ? "border-emerald-500/20 bg-emerald-500/5" : "border-white/5 bg-[#14101c] hover:border-white/15"}`}
          data-testid={`checklist-item-${item.key}`}>
          {item.done ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <Circle className="w-4 h-4 text-slate-600 flex-shrink-0" />}
          <span className={`text-sm ${item.done ? "text-slate-400 line-through" : "text-white"}`}>{item.label}</span>
          {item.done_at && <span className="ml-auto text-[10px] text-slate-500">{item.done_at.slice(0, 10)}</span>}
        </button>
      ))}
    </div>
  );
}

function CandidatesJuryTab({ api, session, onChange }) {
  const [form, setForm] = useState({ nom: "", prenom: "", email: "" });
  const [juryForm, setJuryForm] = useState({ nom: "", role: "Membre", externe: true, email: "" });

  const addCandidate = async () => {
    if (!form.nom || !form.prenom) { toast.error("Nom et prénom requis"); return; }
    await api.post(`/certif/sessions/${session.session_id}/candidates`, form);
    setForm({ nom: "", prenom: "", email: "" });
    toast.success("Candidat ajouté"); onChange();
  };
  const addJury = async () => {
    if (!juryForm.nom) { toast.error("Nom requis"); return; }
    const jury = [...(session.jury || []), juryForm];
    await api.put(`/certif/sessions/${session.session_id}`, { jury });
    setJuryForm({ nom: "", role: "Membre", externe: true, email: "" });
    toast.success("Membre du jury ajouté"); onChange();
  };
  const externes = (session.jury || []).filter(j => j.externe).length;

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <p className="text-sm font-bold text-white mb-3 flex items-center gap-1.5"><Users className="w-4 h-4 text-[#3b82f6]" /> Candidats</p>
        <div className="flex gap-2 mb-3">
          <Input className={inputCls} placeholder="Nom" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} data-testid="candidate-nom-input" />
          <Input className={inputCls} placeholder="Prénom" value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} data-testid="candidate-prenom-input" />
          <Input className={inputCls} placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <Button onClick={addCandidate} className="bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="candidate-add-btn"><Plus className="w-4 h-4" /></Button>
        </div>
        {(session.candidates || []).map(c => (
          <div key={c.candidate_id} className="flex items-center gap-2 py-2 border-t border-white/5" data-testid={`candidate-row-${c.candidate_id}`}>
            <div className="flex-1">
              <p className="text-sm text-white">{c.nom} {c.prenom}</p>
              <p className="text-[10px] text-slate-500">{c.email || "—"}</p>
            </div>
            <label className="flex items-center gap-1.5 text-[10px] text-slate-400 cursor-pointer">
              <input type="checkbox" checked={!!c.dossier_complet}
                onChange={async e => { await api.put(`/certif/candidates/${c.candidate_id}`, { dossier_complet: e.target.checked }); onChange(); }}
                data-testid={`candidate-dossier-${c.candidate_id}`} /> Dossier complet
            </label>
            <label className="flex items-center gap-1.5 text-[10px] text-slate-400 cursor-pointer">
              <input type="checkbox" checked={!!c.emargement}
                onChange={async e => { await api.put(`/certif/candidates/${c.candidate_id}`, { emargement: e.target.checked }); onChange(); }} /> Émargé
            </label>
            <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/certif/candidates/${c.candidate_id}`); onChange(); }}>
              <Trash2 className="w-3.5 h-3.5 text-rose-400" />
            </Button>
          </div>
        ))}
        <div className="mt-3">
          <Button size="sm" variant="outline" className="border-white/10 text-slate-300 text-xs"
            onClick={() => exportDoc(api, session.session_id, "emargement.docx")}
            data-testid="export-emargement-btn">Exporter la feuille d'émargement (DOCX)</Button>
        </div>
      </div>

      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <p className="text-sm font-bold text-white mb-1 flex items-center gap-1.5"><Users className="w-4 h-4 text-[#f7c935]" /> Jury</p>
        <p className={`text-[10px] mb-3 ${(session.jury || []).length >= 3 && externes >= 2 ? "text-emerald-400" : "text-amber-400"}`} data-testid="jury-conformite">
          Conformité : 3 membres minimum dont 2 externes — actuellement {(session.jury || []).length} membre(s), {externes} externe(s)
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Input className={inputCls} placeholder="Nom complet" value={juryForm.nom} onChange={e => setJuryForm({ ...juryForm, nom: e.target.value })} data-testid="jury-nom-input" />
          <Input className={inputCls} placeholder="Email (pour la convocation)" value={juryForm.email} onChange={e => setJuryForm({ ...juryForm, email: e.target.value })} data-testid="jury-email-input" />
          <select value={juryForm.role} onChange={e => setJuryForm({ ...juryForm, role: e.target.value })}
            className="bg-[#0b0811] border border-white/10 rounded-lg text-xs text-white px-2 py-2">
            <option>Président</option><option>Membre</option><option>Responsable session</option>
          </select>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-[10px] text-slate-400 flex-1">
              <input type="checkbox" checked={juryForm.externe} onChange={e => setJuryForm({ ...juryForm, externe: e.target.checked })} /> Externe
            </label>
            <Button onClick={addJury} className="bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="jury-add-btn"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>
        {(session.jury || []).map((j, i) => (
          <div key={i} className="flex items-center gap-2 py-2 border-t border-white/5">
            <div className="flex-1">
              <p className="text-sm text-white">{j.nom} <span className="text-slate-500 text-xs">— {j.role}{j.externe ? " (externe)" : " (interne)"}</span></p>
              <p className="text-[10px] text-slate-500">{j.email || "Pas d'email — convocation email impossible"}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={async () => {
              const jury = session.jury.filter((_, k) => k !== i);
              await api.put(`/certif/sessions/${session.session_id}`, { jury }); onChange();
            }}><Trash2 className="w-3.5 h-3.5 text-rose-400" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export async function exportDoc(api, sessionId, kind) {
  try {
    const r = await api.get(`/certif/sessions/${sessionId}/export/${kind}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = kind;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Export impossible (générez d'abord le contenu)");
  }
}
